import {
    ArrowBackOutlined,
    Box,
    Button,
    CloseOutlined,
    Divider,
    DropdownMenu,
    IconButton,
    Text,
} from '@landr/maestro';
import React from 'react';
import { Trans } from '@lingui/macro';
import { searchV3FilterType } from 'components/Filters/Filters';
import { FiltersParams, TagCategoryParams } from 'types/params';
import SearchToolsDropdownTrigger from './FiltersTrigger/SearchToolsDropdownTrigger';
import KeyFiltersContent from './FiltersContent/KeyFiltersContent';
import TypeFiltersContent from './FiltersContent/TypeFiltersContent';
import BpmFiltersContent from './FiltersContent/BpmFiltersContent';
import useSearchTools from './hooks/useSearchTools';
import useAdvancedFilter from './hooks/useAdvancedFilter';
import Filters from './FiltersContent/AdvancedFiltersContent';
import TagsFiltersContent from './FiltersContent/AdvancedTagsContent';

const dropdownMenuFooterHeight = 80;

const SearchToolsMobileDropdown = ({
    toolsType,
    onClose,
    filtersAppliedTotal,
}: {
    toolsType: searchV3FilterType | FiltersParams | TagCategoryParams;
    onClose: () => void;
    filtersAppliedTotal?: number;
}) => {
    const { clearFilter, isTypeFilter, isBpmFilter, isKeyFilter, resultsCopy } =
        useSearchTools({ toolsType });

    const {
        handleClearCta,
        getFilterResults,
        getFilterName,
        getTagsCategoryState,
        availableAdvancedFilters,
        getIsTagsCategory,
    } = useAdvancedFilter();

    const isAdvancedFilter =
        toolsType === FiltersParams.instruments ||
        toolsType === FiltersParams.genres ||
        toolsType === FiltersParams.sfxs ||
        toolsType === TagCategoryParams.artist ||
        toolsType === TagCategoryParams.timbre ||
        toolsType === TagCategoryParams.mood ||
        toolsType === TagCategoryParams.city ||
        toolsType === TagCategoryParams.music_gear ||
        toolsType === TagCategoryParams.other;

    const searchToolLabel = (() => {
        switch (toolsType) {
            case searchV3FilterType.BPM:
                return <Trans>BPM</Trans>;
            case searchV3FilterType.Key:
                return <Trans>KEY</Trans>;
            case searchV3FilterType.Types:
                return <Trans>TYPE</Trans>;
            default:
                return <Trans>BPM</Trans>;
        }
    })();

    const advancedFilterClearLabel = filtersAppliedTotal ? (
        <Trans>{`Clear all (${filtersAppliedTotal.toString()})`}</Trans>
    ) : (
        <Trans>Clear all </Trans>
    );

    return (
        <DropdownMenu usePortal={false}>
            {isAdvancedFilter ? (
                getFilterResults(toolsType as FiltersParams | TagCategoryParams)
                    .length > 0 && (
                    <SearchToolsDropdownTrigger toolsType={toolsType} />
                )
            ) : (
                <SearchToolsDropdownTrigger toolsType={toolsType} />
            )}
            <DropdownMenu.List>
                <DropdownMenu.Item
                    _hover={{
                        backgroundColor: 'transparent',
                    }}
                    justifyContent="space-between"
                    position="fixed"
                    top="0"
                    left="0"
                    right="0"
                    zIndex="100"
                    backgroundColor="surface.low"
                    borderBottom="1px solid"
                    borderColor="border.high"
                    paddingY="md"
                >
                    <Box display="flex" alignItems="center" gap="md">
                        <IconButton
                            icon={ArrowBackOutlined}
                            label="Back to all filters"
                            isRounded
                            variant="ghost"
                            size="sm"
                        />
                        <Text textStyle="title-xs" fontWeight="bold">
                            {isAdvancedFilter
                                ? getFilterName(toolsType as FiltersParams)
                                : searchToolLabel}
                        </Text>
                    </Box>

                    <IconButton
                        icon={CloseOutlined}
                        label="Close"
                        isRounded
                        variant="ghost"
                        onClick={onClose}
                        size="sm"
                    />
                </DropdownMenu.Item>
                <DropdownMenu.Item
                    width="100%"
                    paddingTop={`${dropdownMenuFooterHeight}px`}
                    closeOnClick={false}
                    _hover={{
                        backgroundColor: 'transparent',
                    }}
                    paddingBottom={`100px`}
                >
                    {isTypeFilter && <TypeFiltersContent />}
                    {isBpmFilter && <BpmFiltersContent />}
                    {isKeyFilter && <KeyFiltersContent isMobile={true} />}

                    {Object.values(availableAdvancedFilters).map((filter) => {
                        if (toolsType === filter) {
                            return getIsTagsCategory(filter) ? (
                                <TagsFiltersContent
                                    filterType={filter}
                                    tags={getFilterResults(filter)}
                                    activeTags={getTagsCategoryState(filter)}
                                    tagsCategory={filter}
                                    key={`mobile-content-${filter}`}
                                />
                            ) : (
                                <Filters filterType={filter} isMobile={true} />
                            );
                        }
                        return null;
                    })}
                </DropdownMenu.Item>

                <DropdownMenu.Item
                    closeOnClick={false}
                    backgroundColor="surface.low"
                    position={'fixed'}
                    width="100%"
                    bottom={0}
                    display={'flex'}
                    flexDirection={'column'}
                    padding="0"
                >
                    <Divider />
                    <Box
                        py="sm"
                        px="lg"
                        display={'flex'}
                        justifyContent={
                            isTypeFilter ? 'flex-end' : 'space-between'
                        }
                        gap={'xs'}
                        width="100%"
                        paddingTop={0}
                    >
                        {!isTypeFilter && (
                            <Button
                                kind="link"
                                variant="secondary"
                                onClick={
                                    isAdvancedFilter
                                        ? handleClearCta
                                        : clearFilter
                                }
                                isDisabled={
                                    isAdvancedFilter && !filtersAppliedTotal
                                }
                            >
                                {isAdvancedFilter ? (
                                    advancedFilterClearLabel
                                ) : (
                                    <Trans>Clear</Trans>
                                )}
                            </Button>
                        )}
                        <Button isRounded onClick={onClose} variant="primary">
                            {`Show ${resultsCopy}`}
                        </Button>
                    </Box>
                </DropdownMenu.Item>
            </DropdownMenu.List>
        </DropdownMenu>
    );
};

export default SearchToolsMobileDropdown;
